"use client";

import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, PenTool, Cog, Factory } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
const steps = [{
  title: "Songwriting & Arrangement",
  description: "Defining the core structure and emotional arc of your tracks to ensure a solid foundation for the recording process.",
  icon: PenTool
}, {
  title: "Recording & Performance",
  description: "Capturing raw energy in the studio with a focus on authentic performances and high-fidelity signal paths.",
  icon: MessageSquare
}, {
  title: "Production & Sound Design",
  description: "Refining the sonic landscape through creative layering, experimental textures, and precise mix engineering techniques.",
  icon: Cog
}, {
  title: "Mastering & Final Polish",
  description: "Final frequency balancing and dynamic control to ensure your music translates perfectly across all playback environments.",
  icon: Factory
}];
export default function CooperationProcess() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height - windowHeight;
      const scrolledDistance = -rect.top;
      if (totalScrollableDistance > 0) {
        let percentage = scrolledDistance / totalScrollableDistance * 100;
        percentage = Math.max(0, Math.min(100, percentage));
        setProgress(percentage);
      } else {
        if (rect.top <= 0) {
          setProgress(100);
        } else {
          setProgress(0);
        }
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  return <section id="the-collaborative-process" ref={sectionRef} className="relative bg-background py-16 md:py-32" data-section-id="224221">
      <div className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-sm py-4 mb-12">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl flex items-center gap-4">
          <span className="text-sm font-semibold font-mono text-foreground whitespace-nowrap uppercase tracking-widest">
            Session Phase
          </span>
          <Progress value={progress} className="h-1 flex-1 bg-muted rounded-none" />
          <span className="text-sm font-medium text-primary font-mono tabular-nums w-12 text-right">
            {Math.round(progress)}%
          </span>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-bold font-default text-foreground mb-4 tracking-tighter uppercase">
            The Collaborative Process
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-sans">
            A tactile, iterative approach to refining your sound while preserving the raw energy and authenticity of your performance.
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-6 md:left-8 top-4 bottom-4 w-px bg-border rounded-none hidden sm:block" />

          <div className="space-y-12 md:space-y-24">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className="relative flex flex-col sm:flex-row items-start gap-6 md:gap-12 group">
                  <div className="relative z-10 flex-shrink-0 flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-none bg-background border border-border group-hover:border-primary transition-colors duration-500 shadow-none sm:ml-0 ml-4">
                    <Icon className="w-5 h-5 md:w-7 md:h-7 text-muted-foreground group-hover:text-primary transition-colors duration-500" />
                  </div>

                  <Card className="flex-1 w-full border-none bg-white/5 rounded-none shadow-none hover:bg-white/10 transition-colors duration-500">
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded-none font-mono uppercase tracking-widest">
                          Phase 0{i + 1}
                        </span>
                      </div>
                      <CardTitle className="text-2xl md:text-3xl font-default text-foreground tracking-tight">
                        {step.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base text-muted-foreground font-sans leading-relaxed">
                        {step.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}